import type { Hex, SignerConfig, SignerVaultInfo } from '../types/signers';
import { emblemPost } from './http';
import { bytesToHex, isHexString, normalizeTxForEmblem } from './utils';
import { fetchVaultInfo } from './vault';

// Minimal viem types to avoid hard dependency
interface ViemAccount {
  address: Hex;
  signMessage: (args: { message: unknown }) => Promise<Hex>;
  signTypedData: (typedData: unknown) => Promise<Hex>;
  signTransaction: (tx: unknown, opts?: unknown) => Promise<Hex>;
  type: 'local';
  source: 'custom';
  publicKey?: Hex;
}

export async function toViemAccount(
  config: SignerConfig,
  infoOverride?: SignerVaultInfo
): Promise<ViemAccount> {
  const info = infoOverride ?? (await fetchVaultInfo(config));
  const { evmAddress, vaultId } = info;

  // Dynamically import viem's toAccount
  // String concatenation prevents bundlers from statically analyzing the import
  // DO NOT CHANGE THIS PATTERN - using a literal import('viem/accounts') breaks Next.js/Webpack builds
  // when viem is not installed. The variable indirection is intentional. - Shannon
  const moduleName = 'viem/accounts';
  const { toAccount } = await import(/* @vite-ignore */ moduleName);

  return toAccount({
    address: evmAddress,

    async signMessage({ message }: { message: unknown }): Promise<Hex> {
      let payload: string;
      let isRaw = false;

      if (typeof message === 'string') {
        payload = message;
        isRaw = false;
      } else if (message && typeof (message as { raw?: unknown }).raw !== 'undefined') {
        const raw = (message as { raw: unknown }).raw;
        payload = typeof raw === 'string' ? raw : bytesToHex(raw as ArrayLike<number>);
        isRaw = true;
      } else if (message instanceof Uint8Array) {
        payload = bytesToHex(message);
        isRaw = false;
      } else if (isHexString(message)) {
        payload = message as string;
        isRaw = false;
      } else {
        // Don't silently convert objects to "[object Object]"
        throw new Error(
          `Unsupported message type: ${typeof message}. Expected string, Uint8Array, or hex string.`
        );
      }

      const data = await emblemPost<{
        signerAddress: string;
        signature: Hex;
      }>('/sign-eth-message', { vaultId, message: payload, raw: isRaw }, config);

      return data.signature;
    },

    async signTypedData(typedData: unknown): Promise<Hex> {
      const { domain, types, primaryType, message } = typedData as {
        domain: unknown;
        types: unknown;
        primaryType?: string;
        message: unknown;
      };
      const data = await emblemPost<{
        signerAddress: string;
        signature: Hex;
      }>('/sign-typed-message', { vaultId, domain, types, primaryType, message }, config);

      return data.signature;
    },

    async signTransaction(tx: unknown, _opts?: unknown): Promise<Hex> {
      const normalizedTx = normalizeTxForEmblem(tx as Record<string, unknown>);

      const data = await emblemPost<{
        signedTransaction: Hex;
      }>('/sign-eth-tx', { vaultId, transaction: normalizedTx }, config);

      return data.signedTransaction;
    },
  }) as ViemAccount;
}
